import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { UserInfo } from "../hooks/useSuperQnaChat";

interface UserInfoFormProps {
  onSubmit: (userInfo: UserInfo) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  onClose?: () => void;
}

/**
 * 사용자 정보 입력 폼 컴포넌트
 */
export const UserInfoForm: React.FC<UserInfoFormProps> = ({
  onSubmit,
  isLoading,
  error,
  onClose,
}) => {
  const [formData, setFormData] = useState<UserInfo>({
    userName: "",
    userPhone: "",
    userEmail: "",
    userCompany: "",
  });

  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // 입력값 변경 핸들러
  const handleChange =
    (field: keyof UserInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      // 실시간 유효성 검사
      if (validationErrors[field]) {
        setValidationErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  // 유효성 검사
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    // 이름 검증
    if (!formData.userName.trim()) {
      errors.userName = "이름을 입력해주세요.";
    } else if (formData.userName.trim().length < 2) {
      errors.userName = "이름은 2글자 이상 입력해주세요.";
    }

    // 전화번호 검증 (숫자만 체크)
    if (!formData.userPhone.trim()) {
      errors.userPhone = "전화번호를 입력해주세요.";
    } else if (!/^\d+$/.test(formData.userPhone.replace(/[-\s]/g, ""))) {
      errors.userPhone = "전화번호는 숫자만 입력해주세요.";
    }

    // 이메일 검증
    if (!formData.userEmail.trim()) {
      errors.userEmail = "이메일을 입력해주세요.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
      errors.userEmail = "올바른 이메일 형식을 입력해주세요.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 폼 제출 핸들러
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const success = await onSubmit(formData);
    if (!success) {
      // 에러는 상위 컴포넌트에서 처리
    }
  };

  // 전화번호 자동 포맷팅
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, "");
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
        7,
        11
      )}`;
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setFormData((prev) => ({ ...prev, userPhone: formatted }));

    if (validationErrors.userPhone) {
      setValidationErrors((prev) => ({ ...prev, userPhone: "" }));
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#f8fafc",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          p: 3,
          textAlign: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        {/* 닫기 버튼 */}
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
                transform: "scale(1.1)",
              },
              transition: "all 0.2s ease",
            }}
            size="small"
            aria-label="닫기"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                border: "2px solid rgba(255,255,255,0.3)",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                AI
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                WORLD-TKDLAB
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Powered by WORLD-TKDLAB{" "}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
            전문적인 상담을 위해 기본 정보를 입력해주세요
          </Typography>
        </Box>
      </Box>

      {/* 폼 컨테이너 */}
      <Box
        sx={{
          p: 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {/* 에러 메시지 */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              "& .MuiAlert-icon": {
                color: "#dc2626",
              },
              "& .MuiAlert-message": {
                color: "#991b1b",
                fontWeight: 500,
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* 입력 필드들 */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* 이름 입력 */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                mb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              👤 이름{" "}
              <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
            </Typography>
            <TextField
              value={formData.userName}
              onChange={handleChange("userName")}
              error={!!validationErrors.userName}
              helperText={validationErrors.userName}
              disabled={isLoading}
              fullWidth
              variant="outlined"
              placeholder="홍길동"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: 3,
                  border: "2px solid #e5e7eb",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  fontSize: "1rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#667eea",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-focused": {
                    borderColor: "#667eea",
                    boxShadow:
                      "0 0 0 3px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-error": {
                    borderColor: "#ef4444",
                    "&:hover, &.Mui-focused": {
                      borderColor: "#ef4444",
                      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
                    },
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "14px 16px",
                  fontSize: "1rem",
                  "&::placeholder": {
                    color: "#9ca3af",
                    opacity: 1,
                  },
                },
                "& .MuiFormHelperText-root": {
                  marginLeft: 0,
                  marginTop: "6px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
          </Box>

          {/* 전화번호 입력 */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                mb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              📱 전화번호{" "}
              <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
            </Typography>
            <TextField
              value={formData.userPhone}
              onChange={handlePhoneChange}
              error={!!validationErrors.userPhone}
              helperText={validationErrors.userPhone}
              disabled={isLoading}
              fullWidth
              variant="outlined"
              placeholder="010-1234-5678"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: 3,
                  border: "2px solid #e5e7eb",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  fontSize: "1rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#667eea",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-focused": {
                    borderColor: "#667eea",
                    boxShadow:
                      "0 0 0 3px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-error": {
                    borderColor: "#ef4444",
                    "&:hover, &.Mui-focused": {
                      borderColor: "#ef4444",
                      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
                    },
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "14px 16px",
                  fontSize: "1rem",
                  "&::placeholder": {
                    color: "#9ca3af",
                    opacity: 1,
                  },
                },
                "& .MuiFormHelperText-root": {
                  marginLeft: 0,
                  marginTop: "6px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
          </Box>

          {/* 이메일 입력 */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                mb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              ✉️ 이메일{" "}
              <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
            </Typography>
            <TextField
              type="email"
              value={formData.userEmail}
              onChange={handleChange("userEmail")}
              error={!!validationErrors.userEmail}
              helperText={validationErrors.userEmail}
              disabled={isLoading}
              fullWidth
              variant="outlined"
              placeholder="user@example.com"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: 3,
                  border: "2px solid #e5e7eb",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  fontSize: "1rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#667eea",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-focused": {
                    borderColor: "#667eea",
                    boxShadow:
                      "0 0 0 3px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-error": {
                    borderColor: "#ef4444",
                    "&:hover, &.Mui-focused": {
                      borderColor: "#ef4444",
                      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
                    },
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "14px 16px",
                  fontSize: "1rem",
                  "&::placeholder": {
                    color: "#9ca3af",
                    opacity: 1,
                  },
                },
                "& .MuiFormHelperText-root": {
                  marginLeft: 0,
                  marginTop: "6px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
          </Box>

          {/* 회사명/도장명 입력 */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#374151",
                mb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              🏢 회사명/도장명{" "}
              <span
                style={{
                  color: "#64748b",
                  marginLeft: "4px",
                  fontSize: "0.875rem",
                }}
              >
                (선택사항)
              </span>
            </Typography>
            <TextField
              value={formData.userCompany}
              onChange={handleChange("userCompany")}
              disabled={isLoading}
              fullWidth
              variant="outlined"
              placeholder="김스태권도"
              helperText="선택사항입니다."
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: 3,
                  border: "2px solid #e5e7eb",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  fontSize: "1rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#667eea",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-focused": {
                    borderColor: "#667eea",
                    boxShadow:
                      "0 0 0 3px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(102, 126, 234, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "14px 16px",
                  fontSize: "1rem",
                  "&::placeholder": {
                    color: "#9ca3af",
                    opacity: 1,
                  },
                },
                "& .MuiFormHelperText-root": {
                  marginLeft: 0,
                  marginTop: "6px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#64748b",
                },
              }}
            />
          </Box>
        </Box>

        {/* 제출 버튼 */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            mt: 3,
            py: 1.8,
            px: 4,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            fontWeight: "bold",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
              boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              background: "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
              boxShadow: "none",
              transform: "none",
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={20} color="inherit" />
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                AI 연결 중...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                🚀 AI 상담 시작하기
              </Typography>
            </Box>
          )}
        </Button>
      </Box>
    </Box>
  );
};
